import { useTranslation } from "react-i18next";
import { sentence } from "@/utils/coach";
import { Badge } from "@/atoms/badge";
import { Button } from "@/atoms/button";
import { FactList, FactRow } from "@/components/fact-row";
import { StoreButtons } from "@/components/store-buttons";
import { addInterval, formatLongDate } from "@/utils/date";
import { formatTableMoney, fromCents } from "@/utils/money";
import type {
  PublicCheckoutProduct,
  PublicCheckoutProductPrice,
} from "@/data/checkout";

/**
 * The done state (checkout 1.4 region `done`; spec receipt 1.1).
 *
 * A receipt, not a celebration. A badge, a headline, one sentence, what happens next, and three
 * facts. No orange (the payment is made, so the one orange has no job left), no tick disc, no
 * confetti and no Dwayne: gate 15.5 keeps him off money, and receipt 1.1 says the moment worth
 * celebrating is the first workout, in Gymkee.
 *
 * Three deliveries, three readings. `gymkee` and `program` both mean the coaching lives in
 * Gymkee, so both get the three numbered steps and the two store buttons. `external` and
 * `service` mean the coach takes it from here, so the coach's own message is shown verbatim in
 * a soft card under a label naming him, and a service adds the appointment fact with its
 * booking link.
 */

export interface ReceiptProps {
  status: "succeeded" | "processing";
  coachName: string;
  email: string;
  /** What was actually paid, in cents. */
  paidCents: number;
  currency: string;
  product: PublicCheckoutProduct;
  price: PublicCheckoutProductPrice;
  isSubscription: boolean;
}

const IN_GYMKEE = new Set(["gymkee", "program", "programme"]);

export function Receipt({
  status,
  coachName,
  email,
  paidCents,
  currency,
  product,
  price,
  isSubscription,
}: ReceiptProps) {
  const { t } = useTranslation();
  const processing = status === "processing";
  const delivery = (product.deliveryMethod ?? "gymkee").toLowerCase();
  const inGymkee = IN_GYMKEE.has(delivery);
  const service = product.serviceConfig;
  const coachMessage = product.postPaymentMessage?.trim();

  const recurring = price.recurring ?? null;
  const nextDate =
    isSubscription && recurring
      ? formatLongDate(
          addInterval(
            new Date(),
            recurring.interval,
            recurring.intervalCount,
          ).toISOString(),
        )
      : null;

  return (
    <section className="gap-gk-6 flex flex-col">
      <div className="gap-gk-4 flex flex-col items-start">
        <Badge tone={processing ? "neutral" : "success"}>
          {processing
            ? t("gymkeePay.common.statusProcessing")
            : t("gymkeePay.common.statusPaid")}
        </Badge>
        <h1 className="text-[32px] leading-[1.15] font-semibold text-[var(--gk-text-primary)]">
          {processing
            ? t("gymkeePay.receipt.headlineProcessing")
            : t("gymkeePay.receipt.headline")}
        </h1>
        {/* One sentence, and only when this page is the one saying it: on an external or a
            service delivery what happens next is the coach's own message, in his words, in the
            soft card below, and our sentence there would be a second voice over his. */}
        <p className="text-[15px] leading-[1.55] text-[var(--gk-text-secondary)]">
          {processing
            ? sentence(
                t("gymkeePay.receipt.lineProcessing", { coach: coachName }),
              )
            : inGymkee
              ? sentence(
                  t("gymkeePay.receipt.lineGymkee", { coach: coachName }),
                )
              : sentence(
                  t("gymkeePay.receipt.lineCoach", { coach: coachName }),
                )}
        </p>
      </div>

      {!inGymkee && coachMessage && (
        <div className="ds-card-soft p-gk-4 gap-gk-2 flex flex-col">
          <span className="text-[13px] text-[var(--gk-text-tertiary)]">
            {t("gymkeePay.receipt.fromCoach", { coach: coachName })}
          </span>
          <p className="text-[15px] leading-[1.55] whitespace-pre-line text-[var(--gk-text-primary)]">
            {coachMessage}
          </p>
        </div>
      )}

      {!inGymkee && service && (
        <FactList>
          <FactRow
            label={t("gymkeePay.receipt.appointment")}
            value={t("gymkeePay.receipt.serviceLine", {
              type: t(serviceTypeKey(service.type)),
              duration: service.duration,
            })}
            action={
              service.calendarLink ? (
                <Button
                  variant="tertiary"
                  href={service.calendarLink}
                  target="_blank"
                >
                  {t("gymkeePay.receipt.bookNow")}
                </Button>
              ) : undefined
            }
          />
        </FactList>
      )}

      {inGymkee && (
        <>
          <div className="ds-card p-gk-4 gap-gk-2 flex flex-col">
            <h2 className="text-[20px] font-semibold text-[var(--gk-text-primary)]">
              {t("gymkeePay.receipt.whatNext")}
            </h2>
            <ol className="flex flex-col">
              {[
                t("gymkeePay.receipt.step1"),
                sentence(t("gymkeePay.receipt.step2", { coach: coachName })),
                sentence(t("gymkeePay.receipt.step3", { coach: coachName })),
              ].map((line, index) => (
                <li
                  key={line}
                  className="gap-gk-3 flex min-h-[40px] items-center text-[14px] text-[var(--gk-text-secondary)]"
                >
                  <span className="ds-pay-step" aria-hidden="true">
                    {index + 1}
                  </span>
                  <span className="min-w-0">{line}</span>
                </li>
              ))}
            </ol>
          </div>
          <StoreButtons />
        </>
      )}

      <FactList>
        <FactRow
          label={t("gymkeePay.receipt.paidToday")}
          value={formatTableMoney(fromCents(paidCents), currency)}
          numeric
        />
        {nextDate && (
          <FactRow
            label={t("gymkeePay.receipt.nextCharge")}
            value={t("gymkeePay.receipt.nextChargeValue", {
              date: nextDate,
              amount: formatTableMoney(fromCents(price.unitAmount), currency),
            })}
          />
        )}
        <FactRow
          label={t("gymkeePay.receipt.receipt")}
          value={email ? t("gymkeePay.receipt.sentTo", { email }) : undefined}
        />
      </FactList>

      <p className="text-[13px] leading-[1.5] text-[var(--gk-text-tertiary)]">
        {isSubscription
          ? t("gymkeePay.receipt.footerSubscription", { coach: coachName })
          : t("gymkeePay.receipt.footerOneOff", { coach: coachName })}
      </p>
    </section>
  );
}

function serviceTypeKey(type: string): string {
  if (type === "visio") return "gymkeePay.receipt.serviceVisio";
  if (type === "mixed") return "gymkeePay.receipt.serviceMixed";
  return "gymkeePay.receipt.serviceInPerson";
}
